/**
 * Extruded polygon floor mesh — replaces axis-aligned box floors for custom footprints.
 */

import * as pc from "playcanvas";
import { polygonSelfIntersects } from "./floor_polygon.js";

const round3 = (v) => Math.round(v * 1000) / 1000;
/** Wood plank repeat in meters — must match surface_library.js. */
const PLANK_M = 1.5;

/** Drop duplicate closing point and coerce to [[x,z], ...]. */
export function normalizeFloorPolygon(polygon) {
  if (!Array.isArray(polygon) || polygon.length < 3) return [];
  const out = [];
  for (const pt of polygon) {
    if (!Array.isArray(pt) || pt.length < 2) continue;
    const x = round3(Number(pt[0]));
    const z = round3(Number(pt[1]));
    if (!Number.isFinite(x) || !Number.isFinite(z)) continue;
    out.push([x, z]);
  }
  if (out.length < 3) return [];
  const [ax, az] = out[0];
  const [bx, bz] = out[out.length - 1];
  if (Math.hypot(ax - bx, az - bz) < 0.001) out.pop();
  if (out.length < 3) return [];
  // CCW when viewed from above (+Y) so top-face normals point up.
  let area = 0;
  for (let i = 0; i < out.length; i++) {
    const [x0, z0] = out[i];
    const [x1, z1] = out[(i + 1) % out.length];
    area += x0 * z1 - x1 * z0;
  }
  if (area < 0) out.reverse();
  return out;
}

function triSignedArea2d(poly) {
  let a = 0;
  for (let i = 0; i < poly.length; i++) {
    const [x0, z0] = poly[i];
    const [x1, z1] = poly[(i + 1) % poly.length];
    a += x0 * z1 - x1 * z0;
  }
  return a / 2;
}

function pointInTriangle2d(p, a, b, c) {
  const sign = (p1, p2, p3) => (p1[0] - p3[0]) * (p2[1] - p3[1]) - (p2[0] - p3[0]) * (p1[1] - p3[1]);
  const d1 = sign(p, a, b);
  const d2 = sign(p, b, c);
  const d3 = sign(p, c, a);
  const hasNeg = d1 < -1e-9 || d2 < -1e-9 || d3 < -1e-9;
  const hasPos = d1 > 1e-9 || d2 > 1e-9 || d3 > 1e-9;
  return !(hasNeg && hasPos);
}

function isEar(poly, iPrev, iCurr, iNext, remaining) {
  const prev = poly[iPrev];
  const curr = poly[iCurr];
  const next = poly[iNext];
  const cross =
    (curr[0] - prev[0]) * (next[1] - curr[1]) - (curr[1] - prev[1]) * (next[0] - curr[0]);
  if (cross <= 1e-9) return false;
  for (const idx of remaining) {
    if (idx === iPrev || idx === iCurr || idx === iNext) continue;
    if (pointInTriangle2d(poly[idx], prev, curr, next)) return false;
  }
  return true;
}

/**
 * Ear-clipping triangulation for simple polygons (convex or concave).
 * Fan triangulation fails on L/T shapes and causes self-overlapping floor meshes.
 */
export function triangulatePolygon(polygon) {
  const poly = normalizeFloorPolygon(polygon);
  if (poly.length < 3) return [];
  if (polygonSelfIntersects(poly.map(([x, z]) => ({ px: x, py: z })))) return [];
  if (poly.length === 3) return [0, 1, 2];

  const remaining = poly.map((_, i) => i);
  const indices = [];
  let guard = 0;

  while (remaining.length > 3 && guard++ < poly.length * poly.length) {
    let clipped = false;
    for (let i = 0; i < remaining.length; i++) {
      const prev = remaining[(i - 1 + remaining.length) % remaining.length];
      const curr = remaining[i];
      const next = remaining[(i + 1) % remaining.length];
      if (!isEar(poly, prev, curr, next, remaining)) continue;
      indices.push(prev, curr, next);
      remaining.splice(i, 1);
      clipped = true;
      break;
    }
    if (!clipped) break;
  }

  if (remaining.length === 3) indices.push(remaining[0], remaining[1], remaining[2]);

  if (indices.length < (poly.length - 2) * 3) {
    console.warn("[floor_mesh] ear clip failed for floor polygon");
    return [];
  }

  return indices;
}

export { triSignedArea2d as _triSignedArea2dForTest };

/**
 * Build an extruded slab mesh for a closed floor polygon (world XZ, Y up).
 * Top face at floorY, bottom at floorY - thickness.
 */
export function buildExtrudedPolygonMesh(device, polygon, floorY, thickness = 0.05) {
  const poly = normalizeFloorPolygon(polygon);
  if (poly.length < 3) return null;

  const asVerts = poly.map(([px, pz]) => ({ px, py: pz }));
  if (polygonSelfIntersects(asVerts)) {
    console.warn("[floor_mesh] self-intersecting floor polygon — skipping mesh");
    return null;
  }

  const n = poly.length;
  const yTop = floorY;
  const yBot = floorY - thickness;
  const positions = [];

  for (const [x, z] of poly) {
    positions.push(x, yTop, z);
  }
  for (const [x, z] of poly) {
    positions.push(x, yBot, z);
  }

  const topTris = triangulatePolygon(poly);
  if (topTris.length < (n - 2) * 3) return null;
  const indices = [...topTris];

  // Bottom face (normals down) — same ear clip as top (not fan).
  for (const tri of topTris) indices.push(tri + n);

  // Side quads.
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    indices.push(i, j, j + n, i, j + n, i + n);
  }

  // World-meter UVs (tiling baked in) so wood maps render on custom meshes.
  const uvs = [];
  for (const [x, z] of poly) uvs.push(x / PLANK_M, z / PLANK_M);
  for (const [x, z] of poly) uvs.push(x / PLANK_M, z / PLANK_M);

  const mesh = new pc.Mesh(device);
  mesh.setPositions(positions);
  mesh.setNormals(pc.calculateNormals(positions, indices));
  mesh.setUvs(0, uvs);
  mesh.setIndices(indices);
  mesh.update(pc.PRIMITIVE_TRIANGLES);
  return mesh;
}

/** Clone floor material with unit tiling — mesh UVs already repeat per meter. */
export function materialForPolygonFloor(material) {
  if (!material?.clone) return material;
  const mat = material.clone();
  mat.diffuseMapTiling = new pc.Vec2(1, 1);
  mat.normalMapTiling = new pc.Vec2(1, 1);
  mat.update();
  return mat;
}

/** True when a material can render a visible floor (textured or solid diffuse). */
export function floorMaterialReady(material) {
  if (!material) return false;
  if (material.diffuseMap) return true;
  const d = material.diffuse;
  return Boolean(d && (d.r > 0.01 || d.g > 0.01 || d.b > 0.01));
}

function polygonFloorEntityReady(entity) {
  const mi = entity?.render?.meshInstances;
  return Boolean(mi?.length && mi[0]?.mesh && floorMaterialReady(mi[0].material));
}

function floorMaterialFromEntity(entity, fallback) {
  const mi = entity.render?.meshInstances?.[0];
  if (mi?.material) return mi.material;
  if (entity.render?.material) return entity.render.material;
  return fallback;
}

/** New floor entity with a custom polygon mesh (vertices in world space). */
export function createPolygonFloorEntity(app, name, polygon, floorY, material, thickness = 0.05) {
  const mesh = buildExtrudedPolygonMesh(app.graphicsDevice, polygon, floorY, thickness);
  if (!mesh) return null;
  const mat = materialForPolygonFloor(material);
  const meshInstance = new pc.MeshInstance(mesh, mat);
  meshInstance.castShadow = false;
  meshInstance.receiveShadow = true;
  const entity = new pc.Entity(name);
  entity.addComponent("render", { meshInstances: [meshInstance] });
  entity.setLocalPosition(0, 0, 0);
  entity.setLocalEulerAngles(0, 0, 0);
  entity.setLocalScale(1, 1, 1);
  return entity;
}

/** Reset a floor entity to an axis-aligned box slab (fallback when polygon mesh fails). */
export function applyBoxFloorMesh(entity, cx, cz, width, depth, floorY, material, thickness = 0.05) {
  if (!entity || !(width > 0) || !(depth > 0)) return false;
  const mat = material ?? floorMaterialFromEntity(entity, null);
  if (!mat) return false;
  if (entity.render) {
    entity.render.type = "box";
    entity.render.meshInstances = [];
    if (mat) entity.render.material = mat;
    entity.render.enabled = true;
    entity.render.castShadows = false;
    entity.render.receiveShadows = true;
  } else {
    entity.addComponent("render", {
      type: "box",
      material: mat,
      castShadows: false,
      receiveShadows: true,
    });
  }
  entity.setLocalEulerAngles(0, 0, 0);
  entity.setLocalScale(width, thickness, depth);
  entity.setLocalPosition(cx, floorY - thickness / 2, cz);
  return true;
}

/** Replace an existing box floor entity with a polygon mesh in place. */
export function applyPolygonFloorMesh(entity, app, polygon, floorY, material, thickness = 0.05) {
  const mesh = buildExtrudedPolygonMesh(app.graphicsDevice, polygon, floorY, thickness);
  if (!mesh || !entity) return false;

  const baseMat = material ?? floorMaterialFromEntity(entity, null);
  if (!baseMat) return false;
  const mat = materialForPolygonFloor(baseMat);

  const meshInstance = new pc.MeshInstance(mesh, mat);
  meshInstance.castShadow = false;
  meshInstance.receiveShadow = true;

  if (entity.render) {
    // Primitive type (e.g. "box") overrides meshInstances — must clear before custom mesh.
    delete entity.render.type;
    entity.render.material = null;
    entity.render.meshInstances = [meshInstance];
    entity.render.enabled = true;
  } else {
    entity.addComponent("render", { meshInstances: [meshInstance] });
  }
  entity.setLocalPosition(0, 0, 0);
  entity.setLocalEulerAngles(0, 0, 0);
  entity.setLocalScale(1, 1, 1);
  return true;
}

/**
 * Floor slab: custom polygon when available, axis-aligned box fallback.
 * @returns {pc.Entity|null}
 */
export function createFloorSlabEntity(app, name, room, floorY, material, opts = {}) {
  const slabOffset = opts.slabOffset ?? 0.004;
  const thickness = opts.thickness ?? 0.05;
  const slabY = floorY + slabOffset;
  if (!floorMaterialReady(material) || !app) return null;

  const b = room?.bounds;
  const min = b?.min ?? [0, 0, 0];
  const max = b?.max ?? [6, 2.8, 6];
  const width = max[0] - min[0];
  const depth = max[2] - min[2];
  const cx = (min[0] + max[0]) / 2;
  const cz = (min[2] + max[2]) / 2;
  if (!(width > 0) || !(depth > 0)) return null;

  const floorPoly = roomFloorPolygon(room);
  let floor = null;
  if (floorPoly?.length >= 3) {
    floor = createPolygonFloorEntity(app, name, floorPoly, slabY, material, thickness);
    if (!polygonFloorEntityReady(floor)) {
      floor?.destroy();
      floor = null;
    }
  }
  if (!floor) {
    floor = new pc.Entity(name);
    if (!applyBoxFloorMesh(floor, cx, cz, width, depth, slabY, material, thickness)) {
      floor.destroy();
      return null;
    }
  }
  if (floor.render) floor.render.enabled = true;
  floor._aspectKind = "floor";
  return floor;
}

/** Resolve floor polygon from room data (floorPolygon or floors[0].polygon). */
export function roomFloorPolygon(room) {
  if (room?.floorPolygon?.length >= 3) return room.floorPolygon;
  if (room?.floors?.[0]?.polygon?.length >= 3) return room.floors[0].polygon;
  return null;
}

/**
 * Push a CCW floor ring outward so the slab buries through wall thickness
 * instead of stopping on the wall line (a see-through slit under the plaster).
 */
export function outsetFloorPolygon(polygon, dist) {
  const poly = normalizeFloorPolygon(polygon);
  const pad = Number(dist) || 0;
  if (poly.length < 3 || pad <= 0) return poly;
  const n = poly.length;
  const out = [];
  for (let i = 0; i < n; i++) {
    const prev = poly[(i - 1 + n) % n];
    const curr = poly[i];
    const next = poly[(i + 1) % n];
    const e0x = curr[0] - prev[0];
    const e0z = curr[1] - prev[1];
    const e1x = next[0] - curr[0];
    const e1z = next[1] - curr[1];
    const l0 = Math.hypot(e0x, e0z) || 1;
    const l1 = Math.hypot(e1x, e1z) || 1;
    // CCW from +Y: outward is right of the edge → (dz, -dx).
    const n0x = e0z / l0;
    const n0z = -e0x / l0;
    const n1x = e1z / l1;
    const n1z = -e1x / l1;
    let nx = n0x + n1x;
    let nz = n0z + n1z;
    const nl = Math.hypot(nx, nz);
    if (nl < 1e-6) {
      nx = n0x;
      nz = n0z;
    } else {
      nx /= nl;
      nz /= nl;
    }
    const miter = Math.min(pad * 3, pad / Math.max(0.2, n0x * nx + n0z * nz));
    out.push([curr[0] + nx * miter, curr[1] + nz * miter]);
  }
  return out;
}
